import React, { FC, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { themeColors, themeSizes } from '../../../styles/globalStyleVars';
import { AlertsDrawerEmptyState } from './AlertsDrawerEmptyState';
import { IconLockAccess } from '@tabler/icons-react';
import { UserContext } from '../../../user/UserContext';
import { UserContextType } from '../../../@types/user';
import { AlertItem } from './AlertItem';
import { AlertInterface } from '../../../interfaces/AlertInterface';
import { callEvent } from '../../../utils/Amplitude/AmplitudeUtilities';
import { useLocationStore } from '../../../stores/locationStore';
import { useAlertsStore } from '../../../stores/alertsStore';
import { ChildCaseCheckbox } from './ChildCaseCheckbox';
import { useGetMinorCases } from '../../../utils/api/requests/useQueries';
import { ChildCasesSuccess } from './ChildCasesSuccess';
import { ChildCaseTicketLink } from './ChildCaseTicketLink';
import { LoadingOverlay } from './LoadingOverlay';
import { TypographyStyled } from '../../Styled/Typography';
import GlobalDrawerButton from '../GlobalDrawerButton';
import { FlowButtonRow } from '../../Equipment/Drawers/ServiceHistory/ServiceHistoryFooter';
import {
  alertsDrawerContentPadding,
  alertsDrawerScrollbarStyles,
} from './AlertsDrawerScrollStyles';

interface Props {
  alertChildCaseOpen: AlertInterface;
}

export type SettledTicket = {
  id: string | undefined;
  locationNum: string;
  error: boolean;
};

const ChildCaseWrapper = styled.div`
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const ChildCaseForm = styled.form`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const ChildCaseScrollableContent = styled.div`
  flex: 1;
  min-height: 0;
  padding: ${alertsDrawerContentPadding};
  border-right: 12px solid transparent;
  ${alertsDrawerScrollbarStyles}
`;

const LocationSelectionWrapper = styled.div`
  margin-bottom: ${themeSizes.xs};
  padding-bottom: 72px;

  > p {
    margin-bottom: ${themeSizes.md};
    color: ${themeColors.colorBlack};
  }

  > div {
    padding-top: ${themeSizes.xs};
    margin-bottom: 20px;
  }
`;

const ChildCaseContentContainer = styled.div`
  flex: 1;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const ChildCaseDrawerContent: FC<Props> = ({ alertChildCaseOpen }) => {
  const { userState } = useContext(UserContext) as UserContextType;
  const { userLocations } = useLocationStore();
  const { setAlertDismissed } = useAlertsStore();
  const {
    data: minorCases,
    isFetching: minorCasesLoading,
    isError: minorCasesError,
    refetch: refetchMinorCases,
  } = useGetMinorCases(
    alertChildCaseOpen?.majorCaseId as string,
    !!alertChildCaseOpen?.majorCaseId
  );
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [settledChildTickets, setSettledChildTickets] = useState<
    SettledTicket[]
  >([]);
  const [showSomeCasesSuccess, setShowSomeCasesSuccess] =
    useState<boolean>(false);
  const [showAllCasesFailed, setShowAllCasesFailed] = useState<boolean>(false);
  const [childCasesLoading, setChildCasesLoading] = useState<boolean>(false);
  const [showChildCaseSuccess, setShowChildCaseSuccess] =
    useState<boolean>(false);
  const [firstChildTicketPath, setFirstChildTicketPath] = useState<
    string | undefined
  >();

  userLocations.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  const locationsNoUpdates = minorCases
    ? userLocations.filter(
        x =>
          !minorCases?.some(
            item =>
              item && 'locationNum' in item && item.locationNum === x.number
          )
      )
    : userLocations;

  const locationsWithUpdates = userLocations.filter(x =>
    minorCases?.some(
      item => item && 'locationNum' in item && item.locationNum === x.number
    )
  );

  const getTicketPath = (locationNum: string, id: string) =>
    `/tickets/${locationNum}/${id}`;

  const getChildTicketPath = () => {
    const firstValid = settledChildTickets.find(
      ticket => ticket.id !== undefined && !ticket.error
    );
    if (firstValid) {
      return getTicketPath(firstValid.locationNum, firstValid.id as string);
    }
    return;
  };

  const getExistingTicketPath = (locationNumber: string) => {
    const caseId = minorCases?.find(
      mc => mc && 'locationNum' in mc && mc.locationNum === locationNumber
    )?.id;
    return getTicketPath(locationNumber, caseId as string);
  };

  useEffect(() => {
    if (
      settledChildTickets.length > 0 &&
      settledChildTickets.length === selectedLocations.length
    ) {
      setChildCasesLoading(false);
      const firstSuccessPath = getChildTicketPath();
      setFirstChildTicketPath(firstSuccessPath);
      const childCaseError = settledChildTickets.some(
        ticket => ticket.error === true
      );
      if (!childCaseError && settledChildTickets.length >= 1) {
        setShowChildCaseSuccess(true);
      } else {
        if (firstSuccessPath) {
          setShowSomeCasesSuccess(true);
        } else {
          setSettledChildTickets([]);
          setShowAllCasesFailed(true);
        }
      }
    }
  }, [settledChildTickets]);

  useEffect(() => {
    setAlertDismissed(alertChildCaseOpen);
    if (alertChildCaseOpen) {
      callEvent('view this affects my restaurant', {
        'alert id': alertChildCaseOpen.id,
        id: alertChildCaseOpen.majorCaseId as string,
        title: alertChildCaseOpen.title,
      });
    }
  }, []);

  const handleCheck = (isSelected: boolean, locationNumber: string) => {
    if (isSelected) {
      setSelectedLocations(prev => [...prev, locationNumber]);
    } else {
      setSelectedLocations(prev => prev.filter(loc => loc !== locationNumber));
    }
  };

  const handleSubmit = () => {
    if (selectedLocations.length === 0 || childCasesLoading) return;

    setChildCasesLoading(true);

    callEvent('submit this affects my restaurant', {
      'alert id': alertChildCaseOpen.id,
      id: alertChildCaseOpen.majorCaseId as string,
      'restaurant number': selectedLocations,
      title: alertChildCaseOpen.title,
    });
  };

  const handleErrorRefetch = () => {
    if (minorCasesError) {
      refetchMinorCases();
      return;
    }
    setChildCasesLoading(true);
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // Prevent native form submit from navigating the app route.
    e.preventDefault();
    handleSubmit();
  };

  return (
    <ChildCaseContentContainer data-testid='alerts-drawer-case-content'>
      {showChildCaseSuccess || showSomeCasesSuccess ? (
        <ChildCasesSuccess
          showChildCaseSuccess={showChildCaseSuccess}
          showSomeErrorsState={showSomeCasesSuccess}
          goBackToChildCase={() => {
            setShowSomeCasesSuccess(false);
            setSettledChildTickets([]);
            setSelectedLocations([]);
            setFirstChildTicketPath(undefined);
          }}
          getChildTicketPath={firstChildTicketPath}
        />
      ) : userState.isStaffOrContractor ? (
        <AlertsDrawerEmptyState
          icon={<IconLockAccess />}
          heading='Only Available to Restaurants'
          description='Sorry, this feature is only available to restaurant users.'
        />
      ) : (
        <ChildCaseWrapper>
          <ChildCaseForm onSubmit={handleFormSubmit}>
            <ChildCaseScrollableContent>
              <TypographyStyled
                text='Stay informed for the issue as updates come in.'
                variant='h3'
                style={{ color: themeColors.colorGray_8 }}
              />
              <TypographyStyled
                style={{
                  margin: `${themeSizes.md} 0 ${themeSizes.lg} 0`,
                  color: `${themeColors.colorGray_7}`,
                }}
                data-testid='child-case-subheading'
              >
                We&apos;ll create a ticket for your Restaurant and send any
                updates on this issue automatically.
              </TypographyStyled>
              <AlertItem alertsDrawerOpen={true} alert={alertChildCaseOpen} />
              <LocationSelectionWrapper id='location-updates-wrapper'>
                {locationsNoUpdates.length > 0 && (
                  <>
                    <TypographyStyled
                      variant='h4'
                      text='Which Restaurants are affected?'
                      color={themeColors.colorGray_8}
                      data-testid='location-updates-heading'
                      style={{ marginBottom: themeSizes.md }}
                    />
                    {locationsNoUpdates.map(location => (
                      <ChildCaseCheckbox
                        key={location.id}
                        location={location}
                        onChange={isSelected =>
                          handleCheck(isSelected, location.number)
                        }
                        submitData={
                          selectedLocations.some(
                            locNum => locNum === location.number
                          ) && childCasesLoading
                        }
                        majorCaseId={alertChildCaseOpen.majorCaseId as string}
                        userGuid={userState.userDetails['cfa-guid']}
                        settledChildTickets={settledChildTickets}
                        setSettledChildTickets={setSettledChildTickets}
                      />
                    ))}
                  </>
                )}
                {locationsWithUpdates.length > 0 && (
                  <>
                    <TypographyStyled
                      variant='h4'
                      text='Restaurants signed up for updates.'
                      color={themeColors.colorGray_8}
                      style={{ marginBottom: themeSizes.smMd }}
                    />
                    <div
                      style={{
                        margin: `${themeSizes.xs} 0 ${themeSizes.xs} ${themeSizes.xs}`,
                      }}
                    >
                      {locationsWithUpdates.map(location => (
                        <ChildCaseTicketLink
                          key={location.id}
                          location={location}
                          ticketPath={getExistingTicketPath(location.number)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </LocationSelectionWrapper>
            </ChildCaseScrollableContent>
            {locationsNoUpdates.length !== 0 && (
              <FlowButtonRow
                style={{ marginTop: 'auto', justifyContent: 'flex-end' }}
                $noMargin
              >
                <GlobalDrawerButton
                  id='location-updates-submit'
                  buttonText='Submit'
                  handleClick={handleSubmit}
                  styles={{ minWidth: '111px', minHeight: '40px' }}
                  disabled={selectedLocations.length === 0}
                  size='sm'
                />
              </FlowButtonRow>
            )}
          </ChildCaseForm>
          <LoadingOverlay
            childCasesLoading={childCasesLoading}
            loadingError={minorCasesError || showAllCasesFailed}
            minorCasesLoading={minorCasesLoading}
            handleErrorRefetch={() => handleErrorRefetch()}
          />
        </ChildCaseWrapper>
      )}
    </ChildCaseContentContainer>
  );
};
